import { inject, Injectable } from '@angular/core';
import { map, Observable, ReplaySubject } from 'rxjs';
import { SpreeService } from './spree-client/storefront/spree.service';
import { YesCategory, YesCategoryCarousel } from '../components/megamenu/megamenu.types';
import { Menus, RelationType } from './spree-client/storefront';
import { MenuItem } from './spree-client/storefront/interfaces/Menu';
import { Icon } from './spree-client/storefront/interfaces/Icon';
import { Params, PRIMARY_OUTLET, Router } from '@angular/router';
import { StateService } from './state.service';

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private readonly client = inject(SpreeService);
  private readonly router = inject(Router);
  private readonly state = inject(StateService);

  private readonly response = new ReplaySubject<
    (YesCategory & {
      location?: string;
    })[]
  >(1);

  constructor() {
    this.client.menus
      .list({ include: 'menu_items,menu_items.icon' })
      .pipe(map((r) => this.deserializeMenu(r)))
      .subscribe((v) => this.response.next(v));
  }

  getHeaderNavigation(): Observable<YesCategory[]> {
    return this.response.pipe(
      map((c) => c.filter((x) => x.location === 'header')[0].children),
    );
  }

  getFooterNavigation(): Observable<YesCategory[]> {
    return this.response.pipe(
      map((c) => c.filter((x) => x.location === 'footer')[0].children),
    );
  }

  deserializeMenu(apiMenu: Menus): YesCategory[] {
    const roots = apiMenu.included
      .filter((item) => isMenuItem(item) && item.attributes.is_root)
      .map((m) => {
        return { location: getMenuLocationByRootId(apiMenu, m.id), id: m.id };
      });

    return (
      roots?.map((root) => {
        return {
          location: root.location,
          ...this.mapMenuRecursively(apiMenu, root.id),
        };
      }) ?? []
    );
  }

  mapMenuRecursively(apiMenu: Menus, childId: string): YesCategory {
    const { relationships, attributes } = (apiMenu.included as MenuItem[]).find(
      (i) => i.id === childId && i.type === 'menu_item',
    )!;

    let queryParams: Params = {};
    let url = attributes.link ?? '';
    const isUrlExternal = url.startsWith('http');

    if (url && !isUrlExternal) {
      const urlTree = this.router.parseUrl(url);
      url = urlTree.root.children[PRIMARY_OUTLET].toString();
      queryParams = urlTree.queryParams;
    }

    const category: YesCategory & Partial<YesCategoryCarousel> = {
      label: attributes.name,
      children: [],
      url,
      queryParams,
      isUrlExternal,
      name: attributes.name,
      carousel: [],
    };

    const iconData = relationships?.icon?.data;

    if (iconData) {
      const icon = apiMenu.included.find(
        (i) => i.id === iconData.id && i.type === iconData.type,
      );

      if (icon) {
        (category as YesCategoryCarousel).imageUrl = this.getImageUrl(icon as Icon);
      }
    }

    const mappedChildren =
      relationships?.children.data.map((c: RelationType) =>
        this.mapMenuRecursively(apiMenu, c.id),
      ) ?? [];

    mappedChildren.forEach((c) => {
      if ('imageUrl' in c) {
        category.carousel.push(c as YesCategoryCarousel);
      } else {
        category.children.push(c);
      }
    });

    return category;
  }

  getImageUrl(icon: Icon): string {
    const imageUrl = icon.attributes.url;

    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }

    return this.state.apiUrl + imageUrl;
  }
}

function isMenuItem(item: MenuItem | Icon): item is MenuItem {
  return item.type === 'menu_item';
}

function getMenuLocationByRootId(apiMenu: Menus, rootId: string): string {
  return apiMenu.data.find((d) =>
    (d.relationships['menu_items']?.data as RelationType[]).find((i) => i.id === rootId),
  )?.attributes.location as string;
}
